import { FC, useEffect, useMemo, useRef, useState } from "react";
import {
  DialogBody,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Button,
  FormDialog,
  FormDialogCancel,
  Input,
  Label,
} from "@capacit-com/lara-law-shared-ui";
import isEqual from "lodash/isEqual";
import type { WidgetTemplate } from "@/models/dashboard/Dashboard";
import {
  WidgetConfig,
  isDynamicListConfig,
} from "@/models/dashboard/WidgetConfig";
import WidgetConfigFields from "../config/WidgetConfigFields";
import DynamicListWidget from "../widgets/DynamicListWidget";
import { getWidgetTypeInfo } from "../lib/widgetCatalog";

interface WidgetTemplateConfigDialogProps {
  template: WidgetTemplate | null;
  onClose: () => void;
  onSave: (defaultName: string, config: WidgetConfig) => void;
}

/**
 * Configures a widget template. A template is not on any grid, so unlike the
 * dashboard's editor panel (LAR-726) this keeps a form copy until "Gem ændringer".
 */
const WidgetTemplateConfigDialog: FC<WidgetTemplateConfigDialogProps> = ({
  template,
  onClose,
  onSave,
}) => {
  const [name, setName] = useState("");
  const [config, setConfig] = useState<WidgetConfig | null>(null);
  const [hasDraft, setHasDraft] = useState(false);
  const initializedTemplateIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (!template) {
      initializedTemplateIdRef.current = null;
      return;
    }
    if (initializedTemplateIdRef.current === template.id) return;

    setName(template.config.defaultName);
    setConfig(structuredClone(template.config.config));
    setHasDraft(false);
    initializedTemplateIdRef.current = template.id;
  }, [template]);

  const hasChanges = useMemo(() => {
    if (!template || !config) return false;
    return (
      name !== template.config.defaultName ||
      !isEqual(config, template.config.config)
    );
  }, [template, name, config]);

  if (!template) return null;

  const type = template.config.type;

  const handleSave = () => {
    if (!config) return;
    onSave(name, config);
    onClose();
  };

  return (
    <FormDialog
      open
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
      // A shortcut typed but not yet added counts as an unapplied edit too.
      isDirty={hasDraft || hasChanges}
      size="xl"
    >
      <DialogHeader>
        <DialogTitle>Konfigurer {getWidgetTypeInfo(type).label}</DialogTitle>
        <DialogDescription>
          Tilpas indstillingerne for denne skabelon.
        </DialogDescription>
      </DialogHeader>

      <DialogBody className="overflow-x-hidden">
        <div className="space-y-6 py-4 px-1">
          <div className="space-y-2">
            <Label htmlFor="template-widget-name">Widget navn</Label>
            <Input
              id="template-widget-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Angiv et navn til denne widget"
            />
          </div>

          {config && (
            <WidgetConfigFields
              type={type}
              config={config}
              onChange={setConfig}
              onDraftStateChange={setHasDraft}
            />
          )}

          {config && isDynamicListConfig(config) && (
            <div className="space-y-2">
              <Label>Forhåndsvisning</Label>
              <div className="table-embed-card h-[320px] overflow-hidden rounded-lg border">
                <DynamicListWidget
                  widgetId={template.id}
                  name={name}
                  config={config}
                />
              </div>
            </div>
          )}
        </div>
      </DialogBody>

      <DialogFooter>
        <FormDialogCancel />
        <Button
          onClick={handleSave}
          disabled={!name.trim() || hasDraft || !hasChanges}
        >
          Gem ændringer
        </Button>
      </DialogFooter>
    </FormDialog>
  );
};

export default WidgetTemplateConfigDialog;
